import React from 'react';
import { Document, Page, Text, StyleSheet, View, Font } from '@react-pdf/renderer';
import { PPDocument, PPTextType } from '@/lib/prostorniPlanoviHelper';

Font.register({
  family: 'Open Sans',
  fonts: [
    { src: '/fonts/OpenSans-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/OpenSans-SemiBold.ttf', fontWeight: 600 },
    { src: '/fonts/OpenSans-Bold.ttf', fontWeight: 700 },
    { src: '/fonts/OpenSans-Italic.ttf', fontWeight: 400, fontStyle: 'italic' },
  ],
});

// PDF styles
const styles = StyleSheet.create({
  page: {
    padding: 55,
    fontSize: 11,
    lineHeight: 1.4,
    fontFamily: 'Open Sans',
  },
  heading: {
    textAlign: 'center',
    fontSize: 17,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionTitle: {
    textAlign: 'center',
    fontSize: 13,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  subSectionTitle: {
    textAlign: 'center',
    fontSize: 12,
    marginBottom: 10,
  },
  clanakTitle: {
    textAlign: 'center',
    fontSize: 11,
    marginBottom: 6,
  },
  paragraph: {
    marginBottom: 0,
  },
  childBlock: {
    marginLeft: 15,
    marginTop: 2,
  },
  header: {
    fontSize: 8,
    fontStyle: 'italic',
    left: 55,
    right: 55,
    top: 30,
    position: 'absolute',
  },
  footer: {
    fontSize: 8,
    fontStyle: 'italic',
    left: 55,
    right: 55,
    top: 800,
    position: 'absolute',
  },
  horizontalLineBottom: {
    borderBottomWidth: 0.6,
    borderBottomColor: '#000',
    borderBottomStyle: 'solid',
  },
  horizontalLineTop: {
    borderTopWidth: 0.6,
    borderTopColor: '#000',
    borderTopStyle: 'solid',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
});

// Header component
const Header = ({ planName }: { planName: string }) => (
  <View style={styles.header} fixed>
    <Text style={{marginBottom: -3}}>{planName} - Isječak prema odabranoj lokaciji</Text>
    <View style={styles.horizontalLineTop} />
  </View>
);

// Footer component with page numbers
const Footer = ({ coordinates }: { coordinates: string[] }) => (
  <View style={styles.footer} fixed>
    <View style={styles.horizontalLineBottom} />
    <View style={styles.headerContent}>
      <Text>Odredbe na lokaciji: {coordinates.join(', ')}</Text>
      <Text render={({ pageNumber, totalPages }) => `Stranica ${pageNumber} od ${totalPages}`} />
    </View>
  </View>
);

// Recursive renderer
const RenderNode: React.FC<{ node: PPTextType }> = ({ node }) => {
  const isTopLevel = /^[0-9]+\.$/.test(node.numeration) && !node.sql_model_name;
  const isSubSection = /^[0-9]+(\.[0-9]+){1,}\.$/.test(node.numeration) && !node.sql_model_name;
  const isClanak = node.sql_model_name === 'OdClanak';

  if (isTopLevel) {
    return (
      <View>
        <Text style={styles.sectionTitle}>
          {node.numeration} {node.value?.toUpperCase()}
        </Text>
        {node.children?.map((child) => (
          <RenderNode key={child.id} node={child} />
        ))}
      </View>
    );
  }

  if (isSubSection) {
    return (
      <View>
        <Text style={styles.subSectionTitle}>
          {node.numeration} {node.value}
        </Text>
        {node.children?.map((child) => (
          <RenderNode key={child.id} node={child} />
        ))}
      </View>
    );
  }

  if (isClanak) {
    return (
      <View>
        <Text style={styles.clanakTitle}>Članak {node.numeration}</Text>
        {node.children?.map((child) => (
          <RenderNode key={child.id} node={child} />
        ))}
      </View>
    );
  }

  return (
    <View style={styles.paragraph}>
      {node.value && (
        <Text>
          {node.numeration ? `${node.numeration} ` : ''}
          {node.value}
        </Text>
      )}

      {node.children && node.children.length > 0 && (
        <View style={styles.childBlock}>
          {node.children.map((child) => (
            <RenderNode key={child.id} node={child} />
          ))}
        </View>
      )}
    </View>
  );
};

interface PdfDocumentProps {
  data: PPDocument;
  clickPosition: string[];
  planName: string;
}

// Main document body
const PPClanciPDF: React.FC<PdfDocumentProps> = ({ data, clickPosition, planName }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Header planName={planName} />
      <Footer coordinates={clickPosition} />

      <Text style={styles.heading}>Odredbe za provedbu</Text>
      {data.map((node) => (
        <RenderNode key={node.id} node={node} />
      ))}
    </Page>
  </Document>
);

export default PPClanciPDF;
